import { memo, useEffect, useMemo, useRef, useState } from "react";

import AccessTimeIcon from "@mui/icons-material/AccessTime";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import { Controller, SubmitHandler, useForm, useWatch } from "react-hook-form";

import { useTheme } from "@mui/material/styles";
import { TimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import ReactDatePicker from "../../../../../../common/components/datePicker/ReactDatePicker";
import { useTimeZoneFromLocationId } from "../../../../../../common/hooks/useTimeZoneFromLocationId";
import {
  errorNotification,
  successNotification,
} from "../../../../../../common/variables/notification";
import TimeZoneInformator from "../../../../../devices/device-details/components/model-settings/TimeZoneInformator";
import { useRetrieveVideo } from "../../../../../telemetry/hooks/useRetrieveVideo";
import LiveViewRecordingsVideoList from "./LiveViewRecordingsVideoList";

dayjs.extend(utc);

dayjs.extend(timezone);

const toTimeZoneStartOfDay = (date: Date, timeZone?: string): Date => {
  if (!timeZone) {
    return dayjs(date).startOf("day").toDate();
  }

  return dayjs(date).tz(timeZone).startOf("day").toDate();
};

interface LiveViewRecordingsTabProps {
  recordingUrl?: string;
  deviceId: string;
  nodeId: string;
}

interface IRecordingsForm {
  selectedDate: Date | null;
  fromTime: dayjs.Dayjs | null;
  duration: number;
}

const LiveViewRecordingsTab = ({
  recordingUrl: _recordingUrl,
  deviceId,
  nodeId,
}: LiveViewRecordingsTabProps): JSX.Element => {
  const [listDate, setListDate] = useState(new Date());
  const [timePickerOpen, setTimePickerOpen] = useState(false);

  const locationId = nodeId.split("#").slice(0, 4).join("#");
  const cleanNodeId = nodeId.replace(/#/g, "_");
  const cleanDeviceId = deviceId.replace(/^DE#/, "");
  const { createVideo } = useRetrieveVideo();
  const theme = useTheme();
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<IRecordingsForm>({
    defaultValues: {
      selectedDate: null,
      fromTime: null,
      duration: 10,
    },
    mode: "onChange",
  });

  const timeZone = useTimeZoneFromLocationId(locationId);
  const didSetDefaultDate = useRef(false);

  const selectedDate = useWatch({ control, name: "selectedDate" });
  const fromTime = useWatch({ control, name: "fromTime" });
  const duration = useWatch({ control, name: "duration" });

  // Ensure the default list date aligns with the device's current day (device TZ),
  // but do not override if the user already changed it from the local default.
  useEffect(() => {
    if (!timeZone) {
      return;
    }

    setListDate(prev => {
      const prevLocalStr = dayjs(prev).format("YYYY-MM-DD");
      const localTodayStr = dayjs().format("YYYY-MM-DD");
      // Only adjust if the current value still equals the local "today"
      // (i.e., initial default). This avoids clobbering user selections.

      if (prevLocalStr !== localTodayStr) {
        return prev;
      }

      const deviceTodayStr = dayjs().tz(timeZone).format("YYYY-MM-DD");

      return dayjs(deviceTodayStr).toDate();
    });
  }, [timeZone]);

  useEffect(() => {
    if (timeZone && !fromTime) {
      const deviceStartOfDay = dayjs().tz(timeZone).startOf("day");

      setValue("fromTime", deviceStartOfDay);
    }
  }, [timeZone, fromTime, setValue]);

  // Default the Start Date picker to the device's current day (device TZ) on load.
  useEffect(() => {
    if (!timeZone) {
      return;
    }

    if (didSetDefaultDate.current || selectedDate) {
      return;
    }

    const deviceTodayStr = dayjs().tz(timeZone).format("YYYY-MM-DD");

    setValue("selectedDate", dayjs(deviceTodayStr).toDate(), {
      shouldValidate: true,
      shouldDirty: false,
    });

    didSetDefaultDate.current = true;
  }, [timeZone, selectedDate, setValue]);

  const minSelectableDate = useMemo(() => {
    const referenceDate = toTimeZoneStartOfDay(new Date(), timeZone);
    const fallback = new Date(
      referenceDate.getTime() - 6 * 24 * 60 * 60 * 1000
    );

    return fallback;
  }, [timeZone]);

  const maxSelectableDate = useMemo(() => {
    if (!timeZone) {
      return dayjs().endOf("day").toDate();
    }
    // Compute the device "today" (by device TZ), then represent that same calendar day
    // in the user's local timezone so the calendar caps by device day, not local day.

    const deviceTodayStr = dayjs().tz(timeZone).format("YYYY-MM-DD");

    return dayjs(deviceTodayStr).endOf("day").toDate();
  }, [timeZone]);

  const shouldDisableTime = (
    timeValue: dayjs.Dayjs,
    view: string | number | Date | dayjs.Dayjs | null | undefined
  ) => {
    const formattedSelectedDate = dayjs(selectedDate).format("YYYY-MM-DD");
    const fallback = timeZone
      ? dayjs().utc().tz(timeZone).format("YYYY-MM-DD")
      : dayjs().utc().format("YYYY-MM-DD");

    const isSelectedDateTodayInTz = fallback === formattedSelectedDate;

    if (!isSelectedDateTodayInTz) {
      return false; // since the selected date is in the past (aka not today), we don't want to disable any times
    }

    const convertedTime = timeZone ? dayjs().utc().tz(timeZone) : dayjs().utc();

    const valueInTz = timeZone ? timeValue.tz(timeZone) : timeValue;

    return valueInTz.isAfter(convertedTime, "minute");
  };

  const filterDateByDeviceTz = useMemo(() => {
    return (date: Date) => {
      if (!timeZone) {
        return (
          dayjs(date).isSame(dayjs(), "day") ||
          dayjs(date).isBefore(dayjs(), "day")
        );
      }

      const deviceNow = dayjs().tz(timeZone);
      const deviceDate = dayjs(date).tz(timeZone);

      return (
        deviceDate.isSame(deviceNow, "day") ||
        deviceDate.isBefore(deviceNow, "day")
      );
    };
  }, [timeZone]);

  const cleanDate = selectedDate
    ? dayjs(selectedDate).format("YYYY/MM/DD")
    : "";

  const { startTime, endTime } = useMemo(() => {
    if (!selectedDate || !fromTime) {
      return { startTime: "", endTime: "" };
    }

    const dateOnly = dayjs(selectedDate).format("YYYY-MM-DD");

    const timePart = fromTime.format("HH:mm");

    const combinedTimestamp = `${dateOnly} ${timePart}:00.000000000`;
    const base = combinedTimestamp.replace(/\.\d+$/, "");

    // Interpret the naive timestamp in the device's time zone, then convert to UTC
    const zoned = timeZone ? dayjs.tz(base, timeZone) : dayjs(base);

    const startUtc = (zoned.isValid() ? zoned : dayjs(base)).utc();

    const startDate = startUtc.format("YYYY-MM-DD_HH-mm-ss");

    const endDate = startUtc
      .add(duration, "minute")
      .format("YYYY-MM-DD_HH-mm-ss");

    return { startTime: startDate, endTime: endDate };
  }, [selectedDate, fromTime, duration, timeZone]);

  const durationOptions = useMemo<number[]>(() => {
    const min = 10;
    const max = 60;
    const step = 5;
    const values: number[] = [];

    for (let v = min; v <= max; v += step) {
      values.push(v);
    }

    return values;
  }, []);

  const onSubmit: SubmitHandler<IRecordingsForm> = async data => {
    if (!nodeId || !deviceId) {
      console.error("Missing nodeId or deviceId");

      errorNotification("Error retrieving video");

      return;
    }

    if (!data.selectedDate || !data.fromTime) {
      return;
    }

    try {
      successNotification("Starting video retrieval");

      const recordingS3Key = `${cleanNodeId}/${cleanDeviceId}/live-view-recordings/${cleanDate}/${cleanDeviceId}_${startTime}_${data.duration}.mp4`;

      await createVideo({
        deviceIdClean: cleanDeviceId,
        nodeIdClean: cleanNodeId,
        s3Key: recordingS3Key,
        startTime,
        endTime,
        videoLength: data.duration,
        timestreamRow: null,
        ddbRow: null,
      });

      setListDate(data.selectedDate);
    } catch (error) {
      console.error("Failed to retrieve video:", error);

      errorNotification("Error retrieving video");
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <TimeZoneInformator timeZone={timeZone} />

      <Box
        sx={{
          width: "100%",
        }}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "flex-start",
              border: `1px solid ${theme.palette.otherOutlineBorder.main}`,
              borderRadius: "8px 8px 0 0",
              padding: "0.5em 1em",
            }}
          >
            <Box sx={{ flex: "0 0 66.67%" }}>
              <Typography
                variant="body1"
                fontSize={14}
                fontWeight={500}
                sx={{ mb: 1, color: theme => theme.palette.text.secondary }}
              >
                Start Date
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                }}
              >
                <Box>
                  <Controller
                    name="selectedDate"
                    control={control}
                    rules={{ required: "Date required" }}
                    render={({ field }) => (
                      <Box sx={{ height: "50px" }}>
                        <ReactDatePicker
                          mode="single"
                          placeholderText="Choose Date"
                          value={field.value}
                          minDate={minSelectableDate}
                          maxDate={maxSelectableDate}
                          filterDate={filterDateByDeviceTz}
                          calendarClassName="no-today-highlight"
                          onChangeRaw={event => {
                            event.preventDefault();
                          }}
                          onChange={value => {
                            if (Array.isArray(value)) {
                              field.onChange(value[0]);
                            } else {
                              field.onChange(value);
                            }
                          }}
                          error={!!errors.selectedDate}
                        />
                        {errors.selectedDate && (
                          <Typography
                            variant="caption"
                            color="error"
                            sx={{ display: "block", mt: 0.5 }}
                          >
                            {errors.selectedDate.message}
                          </Typography>
                        )}
                      </Box>
                    )}
                  />
                </Box>

                <Box
                  sx={{
                    width: { xs: "100%", sm: "50%" },
                    flex: { sm: "0 0 50%" },
                  }}
                >
                  <Controller
                    name="fromTime"
                    control={control}
                    rules={{ required: "Time required" }}
                    render={({ field }) => (
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <TimePicker
                          value={field.value}
                          onChange={value => {
                            field.onChange(value);
                          }}
                          open={timePickerOpen}
                          onOpen={() => setTimePickerOpen(true)}
                          onClose={() => setTimePickerOpen(false)}
                          shouldDisableTime={shouldDisableTime}
                          slotProps={{
                            textField: {
                              size: "small",
                              error: !!errors.fromTime,
                              helperText: errors.fromTime?.message,
                              onClick: () => setTimePickerOpen(true),
                              sx: {
                                width: "100%",
                                cursor: "pointer",
                                "& .MuiInputBase-input": {
                                  cursor: "pointer",
                                },
                              },
                              InputProps: {
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <AccessTimeIcon fontSize="small" />
                                  </InputAdornment>
                                ),
                              },
                            },
                            openPickerIcon: {
                              sx: { display: "none" },
                            },
                            actionBar: {
                              actions: [],
                            },
                          }}
                        />
                      </LocalizationProvider>
                    )}
                  />
                </Box>
              </Box>
            </Box>
            <Box sx={{ flex: "0 0 33.33%" }}>
              <Typography
                variant="body1"
                fontWeight={500}
                fontSize={14}
                sx={{ mb: 1, color: theme => theme.palette.text.secondary }}
              >
                Duration
              </Typography>

              <Controller
                name="duration"
                control={control}
                rules={{ required: "Duration required" }}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.duration}>
                    <Select
                      {...field}
                      value={field.value}
                      onChange={e => field.onChange(e.target.value)}
                      size="small"
                      sx={{
                        width: "100%",
                        height: "40px",
                        borderRadius: "8px",
                      }}
                    >
                      {durationOptions.map(option => (
                        <MenuItem key={option} value={option}>
                          {option}m
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.duration && (
                      <FormHelperText>{errors.duration.message}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Box>
          </Box>

          <Box
            sx={{
              width: "100%",
              border: `1px solid ${theme.palette.otherOutlineBorder.main}`,
              borderTop: "none",
              borderRadius: "0 0 8px 8px",
              padding: "1em",
            }}
          >
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disableElevation
              sx={{
                height: "40px",
                borderRadius: "8px",
                textTransform: "none",
              }}
            >
              Retrieve video
            </Button>
          </Box>
        </form>

        <Box sx={{ width: "100%", px: 0, mt: 2 }}>
          <LiveViewRecordingsVideoList
            deviceId={cleanDeviceId}
            nodeId={cleanNodeId}
            date={cleanDate}
            listDate={listDate}
            setListDate={setListDate}
            timeZone={timeZone}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default memo(LiveViewRecordingsTab);
